/**
 * Project Analytics (Phase 4) — Revenue vs Costs P&L snapshot for Overview tab.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, TrendingDown, IndianRupee, BarChart3 } from 'lucide-react';
import { siteProjectAPI } from '../../services/api';

const fmt = (n) => (Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const sign = (n) => (n >= 0 ? '+' : '−');

export const ProjectAnalytics = ({ projectId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await siteProjectAPI.getAnalytics(projectId);
      setData(r);
    } finally { setLoading(false); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  if (loading || !data) return null;

  const rev = data.revenue?.subtotal || 0;
  const totalCost = data.costs?.total || 0;
  const gp = data.gross_profit || 0;
  const marginPct = data.margin_pct || 0;
  const marginPositive = gp >= 0;

  // Cost composition (for horizontal bar breakdown)
  const costs = data.costs || {};
  const items = [
    { label: 'Material Used', value: costs.material_consumed ?? costs.material_procurement ?? 0, color: 'bg-red-400' },
    { label: 'Labour (Net)', value: costs.labour_net_paid || 0, color: 'bg-amber-400' },
    { label: 'Transport', value: costs.transportation || 0, color: 'bg-indigo-400' },
    { label: 'Expenses', value: costs.expenses || 0, color: 'bg-purple-400' },
  ].filter(x => x.value > 0);
  const costBase = items.reduce((s, x) => s + x.value, 0) || 1;

  const budget = data.budget?.total || 0;
  const budgetUsed = data.budget?.used_pct || 0;
  const budgetColor = budgetUsed < 70 ? 'bg-emerald-500' : budgetUsed < 100 ? 'bg-amber-500' : 'bg-red-500';

  return (
    <div className="pt-3 border-t border-gray-50" data-testid="project-analytics">
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 className="w-4 h-4 text-indigo-600" />
        <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Project P&L Snapshot</p>
      </div>

      {/* Revenue / Costs / Margin tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="p-3 bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl">
          <p className="text-[10px] font-semibold text-emerald-600 uppercase">Revenue (ex-GST)</p>
          <p className="text-lg font-bold text-emerald-700" data-testid="pa-revenue">₹{fmt(rev)}</p>
          <p className="text-[10px] text-emerald-400 mt-0.5">incl GST: ₹{fmt(data.revenue?.with_gst || 0)}</p>
        </div>
        <div className="p-3 bg-gradient-to-br from-red-50 to-rose-50 rounded-xl">
          <p className="text-[10px] font-semibold text-red-500 uppercase">Total Costs</p>
          <p className="text-lg font-bold text-red-600" data-testid="pa-costs">₹{fmt(totalCost)}</p>
          <p className="text-[10px] text-red-400 mt-0.5">Material Used + Labour + Transport + Expenses</p>
        </div>
        <div className={`p-3 rounded-xl bg-gradient-to-br ${marginPositive ? 'from-blue-50 to-indigo-50' : 'from-red-50 to-pink-100'}`}>
          <p className={`text-[10px] font-semibold uppercase ${marginPositive ? 'text-blue-600' : 'text-red-600'}`}>Gross Profit</p>
          <p className={`text-lg font-bold ${marginPositive ? 'text-blue-700' : 'text-red-700'} flex items-center gap-1`} data-testid="pa-gp">
            {marginPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            {sign(gp)}₹{fmt(Math.abs(gp))}
          </p>
          <p className={`text-[10px] mt-0.5 ${marginPositive ? 'text-blue-400' : 'text-red-400'}`}>Margin {marginPct}%</p>
        </div>
        <div className="p-3 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl">
          <p className="text-[10px] font-semibold text-amber-600 uppercase">Labour TDS Deducted</p>
          <p className="text-lg font-bold text-amber-700" data-testid="pa-tds">₹{fmt(costs.labour_tds || 0)}</p>
          <p className="text-[10px] text-amber-400 mt-0.5">Remit to govt</p>
        </div>
      </div>

      {/* Cost composition horizontal bar */}
      {items.length > 0 && (
        <div className="mb-4" data-testid="pa-cost-breakdown">
          <div className="flex justify-between items-end mb-1">
            <p className="text-[10px] font-semibold text-gray-500 uppercase">Cost Composition</p>
            <p className="text-[10px] text-gray-400">Total ₹{fmt(totalCost)}</p>
          </div>
          <div className="flex rounded-lg overflow-hidden h-4 bg-gray-100">
            {items.map((it, i) => (
              <div key={i} className={`${it.color} hover:opacity-90 transition-opacity`} style={{ width: `${(it.value / costBase) * 100}%` }} title={`${it.label}: ₹${fmt(it.value)}`} />
            ))}
          </div>
          <div className="flex flex-wrap gap-3 mt-2">
            {items.map((it, i) => (
              <div key={i} className="flex items-center gap-1.5 text-[11px]">
                <span className={`w-2.5 h-2.5 rounded-sm ${it.color}`} />
                <span className="text-gray-600">{it.label}</span>
                <span className="text-gray-400">₹{fmt(it.value)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Budget progress (if budget set) */}
      {budget > 0 && (
        <div data-testid="pa-budget">
          <div className="flex justify-between items-end mb-1">
            <p className="text-[10px] font-semibold text-gray-500 uppercase">Budget Usage</p>
            <p className="text-[10px] text-gray-500">₹{fmt(totalCost)} of ₹{fmt(budget)} <span className={`ml-1 font-bold ${budgetUsed < 100 ? 'text-emerald-600' : 'text-red-600'}`}>({budgetUsed}%)</span></p>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className={`h-full ${budgetColor} transition-all`} style={{ width: `${Math.min(100, budgetUsed)}%` }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectAnalytics;
